import React from 'react';
import PropTypes from 'prop-types';
import { Redirect } from 'react-router-dom';
import glamorous from 'glamorous';
import Dropdown from 'react-toolbox/lib/dropdown';
import Checkbox from 'react-toolbox/lib/checkbox';
import FontIcon from 'react-toolbox/lib/font_icon';
import Form, {
  formatZipCode,
  formatPhoneNumber,
  scrubNumber,
} from '../../../../common/components/Form';
import ThemedInput, {
  CustomInput,
} from '../../../../common/components/ThemedInput';
import { withLoading } from '../../../../redux-modules/loading-overlay';
import { normalizePropertyPath } from '../../../../common/utils/property-path';
import StatesList from '../../../../common/states.json';
import OptionLists from '../../../../common/option-lists';

const optionsToDropDown = optionArr =>
  optionArr.map(value => {
    const cleanedValue = value.replace(/\s+/g, '');
    return {
      value: cleanedValue,
      label: value,
    };
  });

const AddressInput = glamorous(ThemedInput, {
  forwardProps: ['error', 'label', 'maxLength'],
  rootEl: 'input',
})(props => {
  return {
    '@media screen and (min-width: 426px)': props.showHeader
      ? {
          marginLeft: '3.4rem',
        }
      : {},
  };
});

const AddressDropdown = glamorous(Dropdown, {
  forwardProps: ['source', 'label', 'error'],
  rootEl: 'input',
})(props => {
  return {
    '@media screen and (min-width: 426px)': props.showHeader
      ? {
          marginLeft: '3.4rem',
        }
      : {},
  };
});

const AddressHeader = glamorous.h3(
  {
    marginBottom: '1.5rem',
    verticalAlign: 'bottom',
    display: 'inline-block',
    '@media screen and (max-width: 425px)': {
      fontSize: '1.85rem !important',
    },
  },
  props => {
    const { color } = props;
    if (color) {
      return {
        color,
      };
    }
    return {};
  }
);

const AddressIcon = glamorous(FontIcon)({
  verticalAlign: 'bottom',
  display: 'inline-block',
  width: '3.4rem',
  '@media screen and (max-width: 425px)': {
    width: '2.6rem',
    fontSize: '1.85rem !important',
  },
});

export const SameAsBusinessCheckbox = glamorous(Checkbox)(
  {
    '& > span': { ':hover': { cursor: 'pointer' } },
  },
  props => {
    const { color, checked } = props;
    if (color) {
      const customColor = color;
      const checkedStyles = checked
        ? {
            backgroundColor: customColor,
            borderColor: customColor,
          }
        : {};
      return {
        '& > div:first-of-type': {
          '& > span': { display: 'none' },
          ...checkedStyles,
        },
      };
    }
    return {};
  }
);

export const addressIcons = {
  Business: 'business',
  Mailing: 'markunread_mailbox',
  Home: 'home',
};

export const phoneNumberIcons = {
  Home: 'phone',
  Business: 'phone',
  Mobile: 'smartphone',
};

export const buildAddress = path => {
  return [
    {
      Component: AddressInput,
      name: `${path}.addressLine1`,
      label: 'Address Line 1',
      type: 'text',
      maxLength: 50,
    },
    {
      Component: AddressInput,
      name: `${path}.addressLine2`,
      label: 'Address Line 2',
      type: 'text',
      maxLength: 50,
    },
    {
      Component: AddressInput,
      name: `${path}.city`,
      type: 'text',
      maxLength: 40,
    },
    {
      Component: AddressDropdown,
      name: `${path}.stateCode`,
      label: 'State',
      auto: true,
      source: StatesList.map(state => ({
        value: state.id,
        label: state.name,
      })),
    },
    {
      Component: AddressInput,
      name: `${path}.postalCode`,
      label: 'Zip Code',
      type: 'text',
      onChange: value => formatZipCode(value),
    },
  ];
};

export const renderAddressHeader = (type, color) => () => {
  return (
    <AddressHeader key={`header_${type}`} color={color}>
      <AddressIcon value={addressIcons[type] || ''} />
      {type} Address
    </AddressHeader>
  );
};

const buildPhoneFields = (phoneArr, phonePath, formColor) => {
  return phoneArr.reduce((acc, curr, index) => {
    return [
      ...acc,
      {
        Component: CustomInput,
        'data-iconColor': formColor,
        name: `${phonePath}[${index}].number`,
        label: `${curr.type} Phone`,
        type: 'tel',
        icon: phoneNumberIcons[curr.type],
        primary: true,
        onChange: value => formatPhoneNumber('$1-$2-$3', value),
      },
    ];
  }, []);
};

const buildPersonalFields = (phoneArr, path, formColor) => {
  return[
    {
          Component: Dropdown,
          name: `${path}[0].prefix`,
          auto: true,
          source: optionsToDropDown(
            OptionLists.PrefixOptions.filter(option => option !== 'None')
          ),
          key: 'dd-prefix',
          required: true
        },
        {
          Component: ThemedInput,
          name: `${path}[1].firstName`,
          label: 'First Name',
          type: 'text',
          pattern: '^[A-Za-z\\-\\s\\.]+$',
          'data-validationMsg':
            'Valid names can contain letters, a hyphen, and a period.',
          maxLength: 30,
          required: true,
        },
        {
        Component: ThemedInput,
        name: `${path}[2].lastName`,
        label: 'Last Name',
        type: 'text',
        pattern: '^[A-Za-z\\-\\s\\.]+$',
        'data-validationMsg':
          'Valid names can contain letters, a hyphen, and a period.',
        maxLength: 30,
        required: true,
    }
  ]
};

const buildOrgFields = (phoneArr, path, formColor) => {
  return[
        {
          Component: ThemedInput,
          name: `${path}[0].name`,
          label: 'Business Name',
          type: 'text',
          maxLength: 30,
          required: true,
        }
  ]
};

const getIsRequired = (required, addressType, name) => {
  return (required[addressType] || []).some(requiredField =>
    name.includes(requiredField)
  );
};

const buildAddressSections = (
  addressArr,
  addressesPath,
  required,
  handleCheckboxChange,
  formColor
) => {
  const hasBusinessAddress = addressArr.some(address => {
    return address.type === 'Business';
  });
  return addressArr.reduce((acc, curr, index) => {
    const { type = '' } = curr;
    const checkboxName = `copyBusinessInto${type}`;
    const sameAsBusiness =
      !hasBusinessAddress || type === 'Business'
        ? {}
        : {
            Component: SameAsBusinessCheckbox,
            color: formColor,
            name: checkboxName,
            label: 'Same as Business Address',
            onChange: handleCheckboxChange(type),
            type: 'checkbox',
          };
    const addressFields = buildAddress(
      curr.pathOverride || `${addressesPath}[${index}]`
    ).map(addressField => {
      return {
        ...addressField,
        disabled: checkboxName,
        required: getIsRequired(required, type, addressField.name),
        showHeader: true,
      };
    });
    return [
      ...acc,
      renderAddressHeader(type, formColor),
      sameAsBusiness,
      ...addressFields,
    ];
  }, []);
};

class AddressForm extends React.Component {
  static propTypes = {
    addressData: PropTypes.shape({
      addresses: PropTypes.array,
      phoneNumbers: PropTypes.array,
    }),
    required: PropTypes.objectOf(PropTypes.arrayOf(PropTypes.string)),
    includeHeaders: PropTypes.bool,
    handleSave: PropTypes.func,
    forceReload: PropTypes.bool,
    pageColor: PropTypes.string,
    formWrapper: PropTypes.func,
    children: PropTypes.node,
    renderSubmit: PropTypes.func,
    redirectOnSuccess: PropTypes.string,
  };
  static contextTypes = {
    activateServiceDialog: PropTypes.func,
  };

  constructor(props) {
    super(props);

    this.state = {
      addressData: props.addressData,
      addressSettings: {},
      redirect: null,
      errors: {
        form: [],
      },
    };
  }

  componentWillReceiveProps({ isLoading, addressData, forceReload }) {
    if (isLoading) {
      return;
    }
    if ((!this.props.addressData && addressData) || forceReload) {
      return this.setState(prevState => ({
        ...prevState,
        addressData,
      }));
    }
  }

  handleCopyAddress = addressType => (value, form) => {
    const { addresses, phoneNumbers } = form;
    this.setState(prevState => ({
      ...prevState,
      addressData: {
        addresses,
        phoneNumbers,
      },
      addressSettings: {
        ...prevState.addressSettings,
        [addressType]: {
          sameAsBusiness: value,
        },
      },
    }));
    return value;
  };

  getAddressFieldsWithOverrides = (addressFields, basePath) => {
    const { addressSettings } = this.state;
    const sameAsBusinessList =
      Object.keys(addressSettings || {}).filter(
        key => addressSettings[key].sameAsBusiness
      ) || [];
    if (sameAsBusinessList.length) {
      return addressFields.map(address => {
        const addressType = address.type;
        const businessAddressIndex = addressFields.findIndex(
          address => address.type.toLowerCase() === 'business'
        );
        if (sameAsBusinessList.some(item => item === addressType)) {
          // override path on each type that is marked as same
          return {
            ...address,
            pathOverride: `${basePath}[${businessAddressIndex}]`,
          };
        }
        return address;
      });
    }
    return addressFields;
  };

  handleSubmit = ({ form, isDirty }, onCompleteHandler) => {
    const { handleSave, redirectOnSuccess } = this.props;
    if (!handleSave) {
      return;
    }

    // clear any previous success or errors
    // save changes to state
    this.setState(prevState => ({
      ...prevState,
      addressData: form,
      submitSuccess: false,
      errors: {
        form: [],
      },
    }));

    const { addresses = [], phoneNumbers = [], customers = [], orgDetail = [] } = form;
    const { addressSettings } = this.state;

    // trim any empty addresses (having a type doesn't count as a value)
    let sentAddresses = addresses.filter(address =>
      Object.keys(address).some(
        key => (key === 'type' ? false : address[key] && address[key].length)
      )
    );

    // handle addresses marked same as business
    const sameAsBusinessList =
      Object.keys(addressSettings || {}).filter(
        key => addressSettings[key].sameAsBusiness
      ) || [];
    if (sameAsBusinessList.length) {
      sentAddresses = sentAddresses.map(address => {
        if (sameAsBusinessList.some(item => item === address.type)) {
          const businessAddress = sentAddresses.find(
            sentAddress => sentAddress.type.toLowerCase() === 'business'
          );
          // copy business address into it
          return {
            ...businessAddress,
            type: address.type,
          };
        }
        return address;
      });
    }

    // build phoneNumbers and filter out any empty ones
    const sentPhoneNumbers = phoneNumbers.reduce((acc, curr) => {
      if (curr.number && curr.number.length) {
        return [
          ...acc,
          {
            ...curr,
            number: scrubNumber(curr.number),
          },
        ];
      }
      return acc;
    }, []);

    const sentCustomers = customers.reduce((acc,curr) => {
      acc[curr.key] = curr[curr.key];
      return acc;
    }, {});

    const sentOrgDetail = orgDetail.reduce((acc,curr) => {
      acc[curr.key] = curr[curr.key];
      return acc;
    }, {});

    const results = handleSave(
      { addresses: sentAddresses, phoneNumbers: sentPhoneNumbers, customers: sentCustomers, orgDetail: sentOrgDetail },
      isDirty
    );

    if (typeof results === 'object' && results.payload) {
      const { payload: { resp, json } } = results;
      // handle results
      if (resp && resp.ok) {
        // show success message
        this.setState(prevState => ({
          ...prevState,
          submitSuccess: true,
          redirect: redirectOnSuccess,
        }));

        if (!redirectOnSuccess) {
          // update form info with latest address data
          return onCompleteHandler(false, json);
        }
      } else if (resp && resp.status === 500) {
        return this.context.activateServiceDialog();
      } else if (resp && json) {
        // get any error messages
        this.setState(prevState => ({
          ...prevState,
          errors: {
            ...prevState.errors,
            form:
              json && json.message && json.message.length ? [json.message] : [],
          },
        }));

        // parse detailed errors if they exists
        let errors =
          json && json.detailedErrors
            ? json.detailedErrors.map(error => {
                const errorType = error.propertyName
                  .toLowerCase()
                  .includes('address')
                  ? 'addresses'
                  : 'phoneNumbers';
                let propertyName = normalizePropertyPath(error.propertyName);
                let startIndex = propertyName.lastIndexOf('[');
                if (startIndex >= 0) {
                  const propertyPath = propertyName.slice(
                    startIndex,
                    propertyName.length
                  );
                  propertyName = `${errorType}${propertyPath}`;
                } else {
                  // if there is no index in the error property, assume the error is on the first address or phone
                  startIndex = propertyName.lastIndexOf('.');
                  const propertyPath = propertyName.slice(
                    startIndex,
                    propertyName.length
                  );
                  propertyName = `${errorType}[0]${propertyPath}`;
                }

                return {
                  ...error,
                  propertyName,
                };
              })
            : [];

        return onCompleteHandler(json.message, errors);
      } else if (results.error && results.payload && results.payload.message) {
        return this.setState(prevState => ({
          ...prevState,
          errors: {
            ...prevState.errors,
            form: [results.payload.message],
          },
        }));
      }
    } else if (typeof results === 'boolean' && results) {
      // assume response is okay and save was successful
      this.setState(prevState => ({
        ...prevState,
        submitSuccess: true,
        redirect: redirectOnSuccess,
      }));
    }
  };

  render() {
    const { submitSuccess, errors, redirect } = this.state;

    const errorsObj = {
      success: submitSuccess,
      errors: submitSuccess ? ['Changes were saved successfully'] : errors.form,
    };

    const addressObjPath = 'addresses';

    const {
      children,
      addressData = {},
      required = {},
      pageColor,
      includeHeaders = false,
      includePersonal = false,
      ...restProps
    } = this.props;

    const addressFields = this.getAddressFieldsWithOverrides(
      addressData.addresses || [],
      addressObjPath
    );

    const addressSections = includeHeaders
      ? buildAddressSections(
          addressFields,
          addressObjPath,
          required,
          this.handleCopyAddress,
          pageColor
        )
      : addressFields.reduce((acc, curr, index) => {
          return [
            ...acc,
            ...buildAddress(
              curr.pathOverride || `${addressObjPath}[${index}]`
            ).map(field => ({
              ...field,
              required: getIsRequired(required, curr.type, field.name),
              showHeader: false,
            })),
          ];
        }, []);

    var formInputs = [];

if(includePersonal){
  formInputs = [
      ...buildOrgFields(
        addressData.orgDetail || [],
        'orgDetail',
        pageColor
      ),,
        ...buildPersonalFields(
        addressData.customers || [],
        'customers',
        pageColor
      ),
      ...addressSections,
      ...buildPhoneFields(
        addressData.phoneNumbers || [],
        'phoneNumbers',
        pageColor
      ),
    ];
}else{
formInputs = [
      ...addressSections,
      ...buildPhoneFields(
        addressData.phoneNumbers || [],
        'phoneNumbers',
        pageColor
      ),
    ];
}

    return (
      <div>
        {redirect ? <Redirect push to={redirect} /> : null}
        <Form
          initState={this.state.addressData}
          formInputs={formInputs}
          formSubmit={this.handleSubmit}
          formErrors={errorsObj}
          listenToState
          {...restProps}
        >
          {children}
        </Form>
      </div>
    );
  }
}

export default withLoading(AddressForm);



// WEBPACK FOOTER //
// ./src/App/account/components/AddressForm/index.js